/**
 * AdminFeatureFlagsTab — Phase B.
 *
 * Founder-friendly UI for:
 *   - Feature flags (boolean + multivariate)
 *   - Remote config (string / number / boolean / json)
 *   - Experiments (variants, rollout, stats)
 *   - Audit log (who/what/when/old→new)
 *
 * Targeting presets are first-class: Premium, Founders, Beta, UK, iOS, Android,
 * % rollout. Power users can drop into raw JSON via the "Advanced" toggle.
 *
 * Kill switches are visually prominent (red ring + danger badge) and one tap
 * away. After every save, we fire `duolyst:features-refresh` so any open
 * client SDKs in the same browser refresh immediately.
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Flag, Settings, FlaskConical, History, Plus, Trash2, AlertTriangle,
  Sparkles, ShieldAlert, Crown, BadgeCheck, Globe, Smartphone, Apple,
  Save, Power, Skull, RefreshCw, Edit3,
} from "lucide-react";
import { refreshFeatures } from "@/hooks/useFeatures";

// ── helpers ─────────────────────────────────────────────────────────────

function timeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const sec = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

const TARGETING_PRESETS = [
  { id: "everyone", label: "Everyone", icon: Globe, payload: {} },
  { id: "premium", label: "Premium only", icon: Crown,
    payload: { premium: true } },
  { id: "founders", label: "Founders only", icon: BadgeCheck,
    payload: { founding_member: true } },
  { id: "beta", label: "Beta testers", icon: Sparkles,
    payload: { beta_testers_only: true } },
  { id: "uk", label: "UK only", icon: Globe,
    payload: { country_in: ["GB"] } },
  { id: "ios", label: "iOS only", icon: Apple,
    payload: { platform_in: ["ios"] } },
  { id: "android", label: "Android only", icon: Smartphone,
    payload: { platform_in: ["android"] } },
];

function summarizeTargeting(t) {
  if (!t || Object.keys(t).length === 0) return "Everyone";
  const bits = [];
  if (t.premium === true) bits.push("Premium");
  if (t.founding_member === true) bits.push("Founders");
  if (t.duo_member === true) bits.push("Duo");
  if (t.admins_only) bits.push("Admins");
  if (t.beta_testers_only) bits.push("Beta");
  if (Array.isArray(t.country_in) && t.country_in.length) bits.push(t.country_in.join("/"));
  if (Array.isArray(t.platform_in) && t.platform_in.length) bits.push(t.platform_in.join("/"));
  if (t.min_build) bits.push(`build≥${t.min_build}`);
  if (typeof t.rollout_percent === "number" && t.rollout_percent < 100) bits.push(`${t.rollout_percent}%`);
  return bits.length ? bits.join(" · ") : "Everyone";
}

function tryParseJSON(s, fallback = null) {
  try { return JSON.parse(s); } catch { return fallback; }
}

// ── flags ───────────────────────────────────────────────────────────────

const EMPTY_FLAG = {
  key: "", description: "", category: "general", environment: "all",
  enabled: false, default_value: true, variants: [],
  targeting: {}, dependencies: [], kill_switch: false,
};

function FlagRow({ flag, onEdit, onKill, onUnkill, onDelete }) {
  const killed = flag.kill_switch;
  return (
    <div
      data-testid={`flag-row-${flag.key}`}
      className={`p-3 border rounded-lg flex flex-col sm:flex-row sm:items-center gap-3 ${
        killed ? "border-red-500/60 bg-red-500/5 ring-1 ring-red-500/30" : "border-border bg-card"
      }`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <code className="font-mono text-sm font-semibold truncate">{flag.key}</code>
          {killed && (
            <Badge variant="destructive" className="gap-1">
              <Skull className="w-3 h-3" /> KILLED
            </Badge>
          )}
          {!killed && flag.enabled && <Badge className="bg-emerald-600 hover:bg-emerald-600">ON</Badge>}
          {!killed && !flag.enabled && <Badge variant="outline">OFF</Badge>}
          <Badge variant="secondary" className="text-[10px] uppercase">{flag.environment || "all"}</Badge>
          {flag.category && <Badge variant="outline" className="text-[10px]">{flag.category}</Badge>}
        </div>
        {flag.description && (
          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{flag.description}</p>
        )}
        <div className="text-[11px] text-muted-foreground mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
          <span>🎯 {summarizeTargeting(flag.targeting)}</span>
          {Array.isArray(flag.variants) && flag.variants.length > 0 && (
            <span>🎲 {flag.variants.length} variants</span>
          )}
          {Array.isArray(flag.dependencies) && flag.dependencies.length > 0 && (
            <span>⛓ depends on {flag.dependencies.join(", ")}</span>
          )}
          <span>⏱ {timeAgo(flag.updated_at)}</span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {killed ? (
          <Button
            size="sm" variant="outline"
            data-testid={`flag-unkill-${flag.key}`}
            onClick={() => onUnkill(flag)}
          >
            <Power className="w-4 h-4 mr-1" /> Revive
          </Button>
        ) : (
          <Button
            size="sm" variant="destructive"
            data-testid={`flag-kill-${flag.key}`}
            onClick={() => onKill(flag)}
          >
            <Skull className="w-4 h-4 mr-1" /> Kill
          </Button>
        )}
        <Button size="sm" variant="outline" data-testid={`flag-edit-${flag.key}`} onClick={() => onEdit(flag)}>
          <Edit3 className="w-4 h-4" />
        </Button>
        <Button size="sm" variant="ghost" className="text-red-400" onClick={() => onDelete(flag)}>
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

function TargetingEditor({ value, onChange }) {
  const [advanced, setAdvanced] = useState(false);
  const [jsonText, setJsonText] = useState(JSON.stringify(value || {}, null, 2));
  useEffect(() => { setJsonText(JSON.stringify(value || {}, null, 2)); }, [value]);

  const applyPreset = (p) => onChange({ ...p });

  const setRollout = (v) => onChange({ ...(value || {}), rollout_percent: v });
  const rollout = (value && typeof value.rollout_percent === "number") ? value.rollout_percent : 100;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Targeting presets</Label>
        <button
          type="button"
          onClick={() => setAdvanced((a) => !a)}
          className="text-xs text-primary hover:underline"
          data-testid="targeting-toggle-advanced"
        >
          {advanced ? "Use presets" : "Advanced (JSON)"}
        </button>
      </div>

      {!advanced && (
        <>
          <div className="flex flex-wrap gap-2">
            {TARGETING_PRESETS.map((p) => {
              const Icon = p.icon;
              return (
                <Button
                  key={p.id} size="sm" variant="outline"
                  data-testid={`targeting-preset-${p.id}`}
                  onClick={() => applyPreset(p.payload)}
                  className="gap-1.5"
                >
                  <Icon className="w-3.5 h-3.5" /> {p.label}
                </Button>
              );
            })}
          </div>
          <div className="pt-1">
            <Label className="text-xs">Rollout: {rollout}%</Label>
            <Slider
              value={[rollout]} min={0} max={100} step={1}
              onValueChange={([v]) => setRollout(v)}
              className="mt-2"
              data-testid="targeting-rollout-slider"
            />
          </div>
          <div className="text-xs text-muted-foreground">
            Current rule: <span className="font-medium text-foreground">{summarizeTargeting(value)}</span>
          </div>
        </>
      )}

      {advanced && (
        <Textarea
          value={jsonText}
          onChange={(e) => setJsonText(e.target.value)}
          onBlur={() => {
            const parsed = tryParseJSON(jsonText, null);
            if (parsed && typeof parsed === "object") onChange(parsed);
            else toast.error("Invalid JSON — keeping previous value.");
          }}
          rows={6} className="font-mono text-xs"
          data-testid="targeting-json"
        />
      )}
    </div>
  );
}

function FlagDialog({ open, onClose, initial, onSaved }) {
  const [form, setForm] = useState(EMPTY_FLAG);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(initial?.key);

  useEffect(() => {
    setForm(initial ? { ...EMPTY_FLAG, ...initial } : EMPTY_FLAG);
  }, [initial, open]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.key.trim()) { toast.error("Key is required"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await axios.patch(`${API}/api/admin/feature-flags/${form.key}`, form);
      } else {
        await axios.post(`${API}/api/admin/feature-flags`, form);
      }
      toast.success(isEdit ? "Flag updated" : "Flag created");
      refreshFeatures();
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="flag-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit flag" : "New feature flag"}</DialogTitle>
          <DialogDescription>
            Toggle staged rollouts, kill-switch features, and target specific users.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Key</Label>
              <Input
                value={form.key} onChange={(e) => update("key", e.target.value)}
                disabled={isEdit} placeholder="e.g. globe_projection"
                data-testid="flag-input-key"
              />
            </div>
            <div>
              <Label>Category</Label>
              <Input value={form.category} onChange={(e) => update("category", e.target.value)} placeholder="general" />
            </div>
            <div>
              <Label>Environment</Label>
              <Select value={form.environment || "all"} onValueChange={(v) => update("environment", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="prod">Production</SelectItem>
                  <SelectItem value="staging">Staging</SelectItem>
                  <SelectItem value="dev">Dev only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 pt-6">
              <Switch
                checked={form.enabled} onCheckedChange={(v) => update("enabled", v)}
                data-testid="flag-input-enabled"
              />
              <Label className="cursor-pointer">Enabled</Label>
            </div>
          </div>

          <div>
            <Label>Description</Label>
            <Textarea
              value={form.description} onChange={(e) => update("description", e.target.value)}
              rows={2} placeholder="What does this flag control?"
            />
          </div>

          <TargetingEditor
            value={form.targeting || {}}
            onChange={(t) => update("targeting", t)}
          />

          <div className="border border-red-500/30 rounded-lg p-3 bg-red-500/5">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-red-400 font-semibold flex items-center gap-1.5">
                  <ShieldAlert className="w-4 h-4" /> Kill switch
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  When ON, the flag returns false for everyone — instantly.
                </p>
              </div>
              <Switch
                checked={form.kill_switch}
                onCheckedChange={(v) => update("kill_switch", v)}
                data-testid="flag-input-killswitch"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs">Reason (audit log)</Label>
            <Input
              value={form.reason || ""} onChange={(e) => update("reason", e.target.value)}
              placeholder="Why are you changing this?"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="flag-save-btn">
            <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── remote config ──────────────────────────────────────────────────────

const EMPTY_CONFIG = {
  key: "", description: "", category: "general",
  value_type: "string", default_value: "", value: "",
  targeting: {},
};

function ConfigDialog({ open, onClose, initial, onSaved }) {
  const [form, setForm] = useState(EMPTY_CONFIG);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(initial?.key);

  useEffect(() => {
    setForm(initial ? { ...EMPTY_CONFIG, ...initial } : EMPTY_CONFIG);
  }, [initial, open]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const coerce = (raw, type) => {
    if (raw == null || raw === "") return raw;
    if (type === "number") return Number(raw);
    if (type === "boolean") return raw === true || raw === "true";
    if (type === "json") return tryParseJSON(typeof raw === "string" ? raw : JSON.stringify(raw), raw);
    return String(raw);
  };

  const save = async () => {
    if (!form.key.trim()) { toast.error("Key is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        value: coerce(form.value, form.value_type),
        default_value: coerce(form.default_value, form.value_type),
      };
      if (isEdit) await axios.patch(`${API}/api/admin/remote-config/${form.key}`, payload);
      else await axios.post(`${API}/api/admin/remote-config`, payload);
      toast.success(isEdit ? "Config updated" : "Config created");
      refreshFeatures();
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="config-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit config" : "New remote config"}</DialogTitle>
          <DialogDescription>Set tunable values without redeploying.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Key</Label>
              <Input
                value={form.key} onChange={(e) => update("key", e.target.value)}
                disabled={isEdit} placeholder="e.g. ai_max_suggestions"
                data-testid="config-input-key"
              />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={form.value_type} onValueChange={(v) => update("value_type", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="string">String</SelectItem>
                  <SelectItem value="number">Number</SelectItem>
                  <SelectItem value="boolean">Boolean</SelectItem>
                  <SelectItem value="json">JSON</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Description</Label>
            <Input value={form.description} onChange={(e) => update("description", e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Default value</Label>
              {form.value_type === "boolean" ? (
                <Select
                  value={String(form.default_value ?? false)}
                  onValueChange={(v) => update("default_value", v === "true")}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="false">false</SelectItem>
                    <SelectItem value="true">true</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  value={form.default_value ?? ""}
                  onChange={(e) => update("default_value", e.target.value)}
                />
              )}
            </div>
            <div>
              <Label>Value (if rule matches)</Label>
              {form.value_type === "boolean" ? (
                <Select
                  value={String(form.value ?? false)}
                  onValueChange={(v) => update("value", v === "true")}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="false">false</SelectItem>
                    <SelectItem value="true">true</SelectItem>
                  </SelectContent>
                </Select>
              ) : form.value_type === "json" ? (
                <Textarea
                  value={typeof form.value === "string" ? form.value : JSON.stringify(form.value || {}, null, 2)}
                  onChange={(e) => update("value", e.target.value)}
                  rows={4} className="font-mono text-xs"
                />
              ) : (
                <Input value={form.value ?? ""} onChange={(e) => update("value", e.target.value)} />
              )}
            </div>
          </div>
          <TargetingEditor value={form.targeting || {}} onChange={(t) => update("targeting", t)} />
          <div>
            <Label className="text-xs">Reason (audit log)</Label>
            <Input value={form.reason || ""} onChange={(e) => update("reason", e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="config-save-btn">
            <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── experiments ────────────────────────────────────────────────────────

const EMPTY_EXP = {
  key: "", name: "", description: "", status: "draft",
  variants: [{ name: "control", weight: 50 }, { name: "treatment", weight: 50 }],
  rollout_percent: 100, conversion_event: "",
};

function ExperimentDialog({ open, onClose, initial, onSaved }) {
  const [form, setForm] = useState(EMPTY_EXP);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(initial?.key);

  useEffect(() => {
    setForm(initial ? { ...EMPTY_EXP, ...initial } : EMPTY_EXP);
  }, [initial, open]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const updateVariant = (i, field, val) => {
    const next = [...form.variants];
    next[i] = { ...next[i], [field]: field === "weight" ? Number(val) : val };
    update("variants", next);
  };
  const addVariant = () => update("variants", [...form.variants, { name: `variant_${form.variants.length}`, weight: 50 }]);
  const removeVariant = (i) => update("variants", form.variants.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!form.key.trim()) { toast.error("Key is required"); return; }
    setSaving(true);
    try {
      if (isEdit) await axios.patch(`${API}/api/admin/experiments/${form.key}`, form);
      else await axios.post(`${API}/api/admin/experiments`, form);
      toast.success(isEdit ? "Experiment updated" : "Experiment created");
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="experiment-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit experiment" : "New experiment"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Key</Label>
              <Input
                value={form.key} onChange={(e) => update("key", e.target.value)}
                disabled={isEdit} placeholder="e.g. paywall_copy_v2"
                data-testid="exp-input-key"
              />
            </div>
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => update("name", e.target.value)} />
            </div>
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={form.description} onChange={(e) => update("description", e.target.value)} rows={2} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => update("status", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="running">Running</SelectItem>
                  <SelectItem value="complete">Complete</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Conversion event</Label>
              <Input
                value={form.conversion_event || ""}
                onChange={(e) => update("conversion_event", e.target.value)}
                placeholder="e.g. premium_purchase"
              />
            </div>
          </div>
          <div>
            <Label>Rollout %: {form.rollout_percent}</Label>
            <Slider
              value={[form.rollout_percent]} min={0} max={100} step={1}
              onValueChange={([v]) => update("rollout_percent", v)}
              className="mt-2"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Variants</Label>
              <Button size="sm" variant="outline" onClick={addVariant}>
                <Plus className="w-3.5 h-3.5 mr-1" /> Add variant
              </Button>
            </div>
            <div className="space-y-2">
              {form.variants.map((v, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input
                    value={v.name} onChange={(e) => updateVariant(i, "name", e.target.value)}
                    placeholder="name" className="flex-1"
                  />
                  <Input
                    type="number" min={0} max={100}
                    value={v.weight} onChange={(e) => updateVariant(i, "weight", e.target.value)}
                    className="w-20"
                  />
                  <span className="text-xs text-muted-foreground">%</span>
                  {form.variants.length > 1 && (
                    <Button size="sm" variant="ghost" onClick={() => removeVariant(i)}>
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs">Reason (audit log)</Label>
            <Input value={form.reason || ""} onChange={(e) => update("reason", e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="exp-save-btn">
            <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── audit row ──────────────────────────────────────────────────────────

const ACTION_COLOR = {
  create: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  update: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  delete: "bg-red-500/15 text-red-400 border-red-500/30",
  kill: "bg-red-600/20 text-red-300 border-red-600/40",
  toggle: "bg-amber-500/15 text-amber-400 border-amber-500/30",
};

function diffSnippet(oldV, newV) {
  // Highlight a couple of high-signal fields rather than dumping full json
  const fields = ["enabled", "kill_switch", "rollout_percent", "value", "status"];
  const out = [];
  for (const f of fields) {
    const ov = oldV?.[f] ?? oldV?.targeting?.[f];
    const nv = newV?.[f] ?? newV?.targeting?.[f];
    if (ov !== nv && (ov !== undefined || nv !== undefined)) {
      out.push(`${f}: ${JSON.stringify(ov)} → ${JSON.stringify(nv)}`);
    }
  }
  return out.length ? out.join("  ·  ") : null;
}

function AuditRow({ entry }) {
  const oldV = entry.old_value || null;
  const newV = entry.new_value || null;
  const diff = diffSnippet(oldV, newV);
  return (
    <div className="p-3 border border-border rounded-lg bg-card/50">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className={`uppercase ${ACTION_COLOR[entry.action] || ""}`}>
            {entry.action}
          </Badge>
          <Badge variant="outline" className="text-[10px]">{entry.entity_type}</Badge>
          <code className="text-xs font-mono">{entry.entity_id}</code>
        </div>
        <span className="text-[11px] text-muted-foreground">{timeAgo(entry.created_at)}</span>
      </div>
      <div className="mt-1 text-xs text-muted-foreground">
        by <span className="text-foreground font-medium">{entry.actor_name || entry.actor_email || "unknown"}</span>
        {entry.reason && <> — “{entry.reason}”</>}
      </div>
      {diff && (
        <div className="mt-1 text-[11px] font-mono text-foreground/80 break-words">{diff}</div>
      )}
    </div>
  );
}

// ── main component ─────────────────────────────────────────────────────

export default function AdminFeatureFlagsTab() {
  const [flags, setFlags] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [experiments, setExperiments] = useState([]);
  const [audit, setAudit] = useState([]);
  const [stats, setStats] = useState({});      // exp_key -> stats
  const [loading, setLoading] = useState(true);
  const [editFlag, setEditFlag] = useState(null);
  const [showFlagDialog, setShowFlagDialog] = useState(false);
  const [editConfig, setEditConfig] = useState(null);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [editExp, setEditExp] = useState(null);
  const [showExpDialog, setShowExpDialog] = useState(false);
  const [auditFilter, setAuditFilter] = useState("");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const safe = (p, fb) => p.then((r) => r.data).catch(() => fb);
    const [f, c, e, a] = await Promise.all([
      safe(axios.get(`${API}/api/admin/feature-flags`), { flags: [] }),
      safe(axios.get(`${API}/api/admin/remote-config`), { configs: [] }),
      safe(axios.get(`${API}/api/admin/experiments`), { experiments: [] }),
      safe(axios.get(`${API}/api/admin/feature-audit-log?limit=100`), { entries: [] }),
    ]);
    setFlags(f.flags || []);
    setConfigs(c.configs || []);
    setExperiments(e.experiments || []);
    setAudit(a.entries || []);
    setLoading(false);
  }, []);

  const fetchExpStats = useCallback(async (key) => {
    try {
      const r = await axios.get(`${API}/api/admin/experiments/${key}/stats`);
      setStats((s) => ({ ...s, [key]: r.data }));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => {
    experiments.forEach((e) => fetchExpStats(e.key));
  }, [experiments, fetchExpStats]);

  const onKill = async (flag) => {
    if (!window.confirm(`Kill "${flag.key}"? It will return false for everyone.`)) return;
    try {
      await axios.patch(`${API}/api/admin/feature-flags/${flag.key}`,
        { kill_switch: true, reason: "Admin kill switch" });
      toast.success(`🪦 Killed ${flag.key}`);
      refreshFeatures();
      fetchAll();
    } catch (e) { toast.error("Failed to kill"); }
  };

  const onUnkill = async (flag) => {
    try {
      await axios.patch(`${API}/api/admin/feature-flags/${flag.key}`,
        { kill_switch: false, reason: "Admin revive" });
      toast.success(`Revived ${flag.key}`);
      refreshFeatures();
      fetchAll();
    } catch (e) { toast.error("Failed to revive"); }
  };

  const onDeleteFlag = async (flag) => {
    if (!window.confirm(`Permanently delete "${flag.key}"?`)) return;
    try {
      await axios.delete(`${API}/api/admin/feature-flags/${flag.key}`);
      toast.success("Deleted");
      refreshFeatures();
      fetchAll();
    } catch { toast.error("Delete failed"); }
  };

  const onDeleteConfig = async (cfg) => {
    if (!window.confirm(`Delete config "${cfg.key}"?`)) return;
    try {
      await axios.delete(`${API}/api/admin/remote-config/${cfg.key}`);
      toast.success("Deleted");
      refreshFeatures();
      fetchAll();
    } catch { toast.error("Delete failed"); }
  };

  const filteredAudit = useMemo(() => {
    if (!auditFilter) return audit;
    return audit.filter((a) => a.entity_type === auditFilter);
  }, [audit, auditFilter]);

  const killCount = flags.filter((f) => f.kill_switch).length;
  const enabledCount = flags.filter((f) => f.enabled && !f.kill_switch).length;

  return (
    <div className="space-y-4" data-testid="admin-feature-flags">
      {/* Top KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard icon={<Flag className="w-4 h-4 text-primary" />} label="Flags" value={flags.length} />
        <KpiCard icon={<Power className="w-4 h-4 text-emerald-500" />} label="Enabled" value={enabledCount} />
        <KpiCard
          icon={<Skull className="w-4 h-4 text-red-500" />}
          label="Killed" value={killCount}
          accent={killCount > 0 ? "red" : null}
        />
        <KpiCard icon={<FlaskConical className="w-4 h-4 text-pink-500" />} label="Experiments" value={experiments.length} />
      </div>

      <Tabs defaultValue="flags" className="space-y-4">
        <div className="overflow-x-auto -mx-4 px-4">
          <TabsList className="bg-card border border-border inline-flex min-w-max">
            <TabsTrigger value="flags" data-testid="tab-flags"><Flag className="w-3.5 h-3.5 mr-1" />Flags</TabsTrigger>
            <TabsTrigger value="config" data-testid="tab-config"><Settings className="w-3.5 h-3.5 mr-1" />Remote Config</TabsTrigger>
            <TabsTrigger value="experiments" data-testid="tab-experiments"><FlaskConical className="w-3.5 h-3.5 mr-1" />Experiments</TabsTrigger>
            <TabsTrigger value="audit" data-testid="tab-audit"><History className="w-3.5 h-3.5 mr-1" />Audit Log</TabsTrigger>
          </TabsList>
        </div>

        {/* FLAGS */}
        <TabsContent value="flags" className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {flags.length} flag{flags.length === 1 ? "" : "s"}
              {killCount > 0 && (
                <span className="ml-2 text-red-400">· {killCount} killed</span>
              )}
            </p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={fetchAll} disabled={loading}>
                <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </Button>
              <Button size="sm" data-testid="new-flag-btn" onClick={() => { setEditFlag(null); setShowFlagDialog(true); }}>
                <Plus className="w-4 h-4 mr-1" /> New flag
              </Button>
            </div>
          </div>
          {flags.length === 0 && !loading && (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground text-sm">
                No flags yet. Create your first to start staged rollouts.
              </CardContent>
            </Card>
          )}
          <div className="space-y-2">
            {flags.map((f) => (
              <FlagRow
                key={f.id || f.key}
                flag={f}
                onEdit={(fl) => { setEditFlag(fl); setShowFlagDialog(true); }}
                onKill={onKill} onUnkill={onUnkill}
                onDelete={onDeleteFlag}
              />
            ))}
          </div>
        </TabsContent>

        {/* REMOTE CONFIG */}
        <TabsContent value="config" className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">{configs.length} config keys</p>
            <Button size="sm" data-testid="new-config-btn" onClick={() => { setEditConfig(null); setShowConfigDialog(true); }}>
              <Plus className="w-4 h-4 mr-1" /> New config
            </Button>
          </div>
          {configs.length === 0 && !loading && (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground text-sm">
                No remote config keys yet.
              </CardContent>
            </Card>
          )}
          <div className="space-y-2">
            {configs.map((c) => (
              <div
                key={c.id || c.key}
                className="p-3 border border-border bg-card rounded-lg flex flex-col sm:flex-row sm:items-center gap-3"
                data-testid={`config-row-${c.key}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <code className="font-mono text-sm font-semibold">{c.key}</code>
                    <Badge variant="outline" className="text-[10px] uppercase">{c.value_type}</Badge>
                    {c.category && <Badge variant="secondary" className="text-[10px]">{c.category}</Badge>}
                  </div>
                  {c.description && <p className="text-xs text-muted-foreground mt-0.5">{c.description}</p>}
                  <div className="text-[11px] text-muted-foreground mt-0.5 flex flex-wrap gap-x-3">
                    <span>default: <code className="text-foreground">{JSON.stringify(c.default_value)}</code></span>
                    <span>match: <code className="text-foreground">{JSON.stringify(c.value)}</code></span>
                    <span>🎯 {summarizeTargeting(c.targeting)}</span>
                    <span>⏱ {timeAgo(c.updated_at)}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => { setEditConfig(c); setShowConfigDialog(true); }}>
                    <Edit3 className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-400" onClick={() => onDeleteConfig(c)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* EXPERIMENTS */}
        <TabsContent value="experiments" className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">{experiments.length} experiment{experiments.length === 1 ? "" : "s"}</p>
            <Button size="sm" data-testid="new-exp-btn" onClick={() => { setEditExp(null); setShowExpDialog(true); }}>
              <Plus className="w-4 h-4 mr-1" /> New experiment
            </Button>
          </div>
          {experiments.length === 0 && !loading && (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground text-sm">
                No experiments yet.
              </CardContent>
            </Card>
          )}
          <div className="space-y-3">
            {experiments.map((e) => {
              const s = stats[e.key];
              const winner = s?.winner;
              return (
                <Card key={e.id || e.key} data-testid={`exp-card-${e.key}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <CardTitle className="text-base flex items-center gap-2">
                        <FlaskConical className="w-4 h-4 text-pink-500" />
                        {e.name || e.key}
                        <code className="text-[11px] font-mono text-muted-foreground ml-1">{e.key}</code>
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        <Badge variant={e.status === "running" ? "default" : "outline"}>{e.status}</Badge>
                        <Badge variant="secondary" className="text-[10px]">{e.rollout_percent ?? 100}% rollout</Badge>
                        <Button size="sm" variant="outline" onClick={() => { setEditExp(e); setShowExpDialog(true); }}>
                          <Edit3 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    {e.description && <p className="text-xs text-muted-foreground">{e.description}</p>}
                  </CardHeader>
                  <CardContent>
                    {!s || !s.variants || s.variants.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No data yet.</p>
                    ) : (
                      <div className="space-y-2">
                        {s.variants.map((v) => {
                          const isWinner = winner && v.variant === winner && v.impressions > 0;
                          const pct = (v.conversion_rate * 100).toFixed(1);
                          return (
                            <div key={v.variant} className="flex items-center gap-3">
                              <div className="w-28 truncate text-sm font-medium">
                                {v.variant}
                                {isWinner && <span className="ml-1 text-amber-400">★</span>}
                              </div>
                              <div className="flex-1">
                                <div className="h-2 rounded-full bg-muted overflow-hidden">
                                  <div
                                    className={`h-full ${isWinner ? "bg-amber-400" : "bg-primary"}`}
                                    style={{ width: `${Math.min(100, v.conversion_rate * 100 * 4)}%` }}
                                  />
                                </div>
                              </div>
                              <div className="w-44 text-right text-xs text-muted-foreground tabular-nums">
                                {v.conversions} / {v.impressions} · <span className="text-foreground font-semibold">{pct}%</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* AUDIT */}
        <TabsContent value="audit" className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Label className="text-xs">Filter:</Label>
            {["", "flag", "config", "experiment"].map((opt) => (
              <Button
                key={opt || "all"}
                size="sm"
                variant={auditFilter === opt ? "default" : "outline"}
                onClick={() => setAuditFilter(opt)}
                data-testid={`audit-filter-${opt || "all"}`}
              >
                {opt ? `${opt}s` : "All"}
              </Button>
            ))}
            <Button size="sm" variant="ghost" onClick={fetchAll} className="ml-auto">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
          {filteredAudit.length === 0 && (
            <Card><CardContent className="py-6 text-center text-sm text-muted-foreground">
              No audit entries{auditFilter ? ` for ${auditFilter}` : ""} yet.
            </CardContent></Card>
          )}
          <div className="space-y-2">
            {filteredAudit.map((a) => <AuditRow key={a.id} entry={a} />)}
          </div>
        </TabsContent>
      </Tabs>

      <FlagDialog open={showFlagDialog} initial={editFlag} onClose={() => setShowFlagDialog(false)} onSaved={fetchAll} />
      <ConfigDialog open={showConfigDialog} initial={editConfig} onClose={() => setShowConfigDialog(false)} onSaved={fetchAll} />
      <ExperimentDialog open={showExpDialog} initial={editExp} onClose={() => setShowExpDialog(false)} onSaved={fetchAll} />
    </div>
  );
}

// Tiny KPI chip ─────────────────────────────────────────────────────────
function KpiCard({ icon, label, value, accent }) {
  const ring = accent === "red"
    ? "ring-1 ring-red-500/40 bg-red-500/5" : "";
  return (
    <Card className={ring}>
      <CardContent className="p-3 flex items-center justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
          <p className="text-2xl font-bold tabular-nums">{value}</p>
        </div>
        {icon}
      </CardContent>
    </Card>
  );
}
